import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, ViewChildren, QueryList } from '@angular/core';
import { AgrimotorService } from '../agrimotor.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AgVehicleFormComponent } from './vehicle-form/vehicle-form.component';

@Component({
  selector: 'app-ag-vehicles',
  templateUrl: './vehicles.component.html',
  styleUrls: ['./vehicles.component.css', '../../quote.component.css']
})
export class AgrimotorVehiclesComponent implements OnInit, OnChanges {
  @Input() staticData;
  @Input() modelObj;
  @Input() authDetails;
  @Input() apiModelObj;
  @Output() tabChanged = new EventEmitter();
  @Output() modelObjUpdated = new EventEmitter();
  @ViewChildren(AgVehicleFormComponent) vehicleForms: QueryList<AgVehicleFormComponent>;

  vehicles: any[] = [];
  declarationAccepted: boolean = false;
  declarationTouched: boolean = false;
  expandAll: boolean = false;

  // Top-of-page declaration questions. Answering one writes the equivalent answer onto
  // every vehicle so the common case doesn't have to be repeated per vehicle; each
  // vehicle can still be overridden individually afterwards.
  declarationQuestions: any[] = [
    {
      key: 'comprehensive',
      text: 'Are all vehicles to be insured for Comprehensive cover?',
      answer: '',
    },
    {
      key: 'owned_by_proposer',
      text: 'Are all vehicles owned by & registered to the Proposer?',
      answer: '',
    },
    {
      key: 'kept_overnight',
      text: 'Are all vehicles kept overnight at the correspondence address of the proposer at least 330 days per year?',
      answer: '',
    },
    {
      key: 'green_card',
      text: 'Does any vehicle require annual green card?',
      answer: '',
      help: 'A green card is proof of insurance when driving in some countries outside the UK.',
    },
    {
      key: 'extended_use',
      text: 'Is there a requirement to extend the use of any vehicle to include personal use e.g business use on behalf '
        + 'of another person (or company) or to travel to/or from a place of work or study, that is not connected to the '
        + 'business of the proposer?',
      answer: '',
    },
  ];

  constructor(private agService: AgrimotorService, private _snackBar: MatSnackBar) { }

  ngOnInit(): void {
    this.initializeVehicles();
    this.declarationAccepted = this.restoreDeclarationAccepted();
    this.restoreDeclarationAnswers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const application = this.agService.applicationOf(this.apiModelObj);
    if (this.apiModelObj && application) {
      const meta = this.agService.quoteMetaOf(this.apiModelObj);
      this.modelObj = application;
      this.modelObj.id = meta.id;
      this.modelObj.reference = meta.reference;
      this.modelObj.status = meta.status;
      this.modelObj.sequence_number = this.apiModelObj.sequence_number;
      this.modelObj.company_id = this.apiModelObj.company_id;
      if (this.apiModelObj.financial_details) {
        this.modelObj.broker_commission = this.apiModelObj.financial_details.broker_commission;
        this.modelObj.broker_admin_fee = this.apiModelObj.financial_details.broker_admin_fee;
      }
    }
    // Re-initialize vehicles when modelObj is updated from parent (e.g. after proposer save)
    if (changes.modelObj && !changes.modelObj.firstChange && this.modelObj) {
      this.initializeVehicles();
      this.declarationAccepted = this.restoreDeclarationAccepted();
      this.restoreDeclarationAnswers();
    }
  }

  // The API stores vehicle drivers as bare id strings; the form needs the full
  // driver record so it can show the name. Accepts either representation.
  private resolveDrivers(list: any): any[] {
    return (list || []).map((d: any) => {
      const id = (typeof d === 'string') ? d : d?.id;
      if (!id) { return d; }
      const full = (this.modelObj.drivers || []).find(dr => dr.id === id);
      if (full) { return { ...full }; }
      return (typeof d === 'string') ? { id } : d;
    }).filter(d => !!d);
  }

  // Outgoing direction: always a flat list of unique id strings.
  private toDriverIds(list: any): string[] {
    const ids = (list || [])
      .map((d: any) => (typeof d === 'string') ? d : d?.id)
      .filter((id: any) => !!id);
    return Array.from(new Set<string>(ids));
  }

  initializeVehicles() {
    this.vehicles = [];

    // 1. Try new vehicles array first
    if (this.modelObj.vehicles && this.modelObj.vehicles.length > 0) {
      this.vehicles = this.modelObj.vehicles.map(v => {
        const vehicle = { ...v };
        // Ensure drivers array exists; resolve ID-only references to full objects
        vehicle.drivers = this.resolveDrivers(vehicle.drivers);
        vehicle.extended_drivers = this.resolveDrivers(vehicle.extended_drivers);
        return vehicle;
      });
    } else {
      // 2. Fall back to legacy typed arrays (for old saved quotes)
      (this.modelObj.agricultural_vehicles || []).forEach(v => this.vehicles.push({ ...v, type: 'agricultural' }));
      (this.modelObj.cars || []).forEach(v => this.vehicles.push({ ...v, type: 'cars' }));
      (this.modelObj.commercial_vehicles || []).forEach(v => this.vehicles.push({ ...v, type: 'commercial' }));
      (this.modelObj.horseboxes || []).forEach(v => this.vehicles.push({ ...v, type: 'horseboxes' }));
      (this.modelObj.trailers || []).forEach(v => this.vehicles.push({ ...v, type: 'trailers' }));
    }

    // 3. If still empty, create from vehicle_counts
    if (this.vehicles.length === 0 && this.modelObj.vehicle_counts) {
      const vc = this.modelObj.vehicle_counts;
      for (let i = 0; i < (vc.agricultural || 0); i++) { this.vehicles.push(this.emptyVehicle('agricultural')); }
      for (let i = 0; i < (vc.cars || 0); i++) { this.vehicles.push(this.emptyVehicle('cars')); }
      for (let i = 0; i < (vc.commercial || 0); i++) { this.vehicles.push(this.emptyVehicle('commercial')); }
      for (let i = 0; i < (vc.horseboxes || 0); i++) { this.vehicles.push(this.emptyVehicle('horseboxes')); }
      for (let i = 0; i < (vc.trailers || 0); i++) { this.vehicles.push(this.emptyVehicle('trailers')); }
    }

    // 4. Always ensure at least one vehicle
    if (this.vehicles.length === 0) {
      this.vehicles.push(this.emptyVehicle('horseboxes'));
    }
  }

  emptyVehicle(type: string) {
    return {
      type,
      registration_number: '', vin: '', make: '', model: '', year: '',
      body_type: '', weight: '', value: null, cover_type: '',
      is_road_registered: '', road_registered: false,
      is_limited_use: '', limited_use: false,
      driving_restriction: '', mileage: '', tracker: '', ncd: '',
      extended_use: { driver_business_use: false, livestock_carriage: false, contract_work: false, horse_transporter: '' },
      green_card: '', is_registered_owner: '', owner: '', owner_name: '',
      is_kept_overnight: '', postcode: '', overnight_addresses: [], has_modifications: '', mods: [],
      drivers: [], extended_drivers: [],
    };
  }

  // ─── Declaration questions ─────────────────────────────────────────────────
  // Writing straight onto the vehicle objects is enough: each vehicle-form patches
  // itself from its `vehicle` input on change, so the answers appear in the individual
  // vehicle panels and remain editable there.
  applyDeclarationAnswer(question: any) {
    if (!question.answer) { return; }
    const yes = question.answer === 'Yes';

    this.vehicles.forEach(v => {
      if (v.type === 'trailers') { return; }
      switch (question.key) {
        case 'comprehensive':
          if (yes) { v.cover_type = 'Comprehensive'; }
          break;
        case 'owned_by_proposer':
          v.is_registered_owner = yes ? 'Yes' : 'No';
          if (yes) { v.owner = ''; v.owner_name = ''; }
          break;
        case 'kept_overnight':
          v.is_kept_overnight = yes ? 'Yes' : 'No';
          if (yes) { v.postcode = ''; v.overnight_addresses = []; }
          break;
        case 'green_card':
          // The form reads this back through toYesNo(), so the boolean is enough.
          v.green_card = yes;
          break;
        case 'extended_use':
          if (!v.extended_use) { v.extended_use = {}; }
          // Only meaningful for the classes that offer the option.
          if (['horseboxes', 'cars', 'commercial'].includes(v.type)) {
            v.extended_use.driver_business_use = yes;
          }
          break;
      }
    });

    this.modelObj.vehicle_declaration_answers = this.declarationQuestions
      .reduce((acc, q) => { acc[q.key] = q.answer; return acc; }, {});

    // Re-run each vehicle form's patch/validators against the updated objects.
    if (this.vehicleForms) {
      this.vehicleForms.forEach(vf => vf.refreshFromVehicle());
    }
  }

  /**
   * The agri-api drops `vehicle_declaration` — it is not part of the application schema, so it
   * comes back undefined on every edit and the tick box rendered unchecked on a quote that had
   * plainly been declared.
   *
   * A saved vehicle list is proof the box was ticked: nextTab() refuses to leave this page while
   * declarationAccepted is false, so the quote could not have reached the API with vehicles on it
   * otherwise. A quote saved only as far as the Proposer page has no vehicles yet and correctly
   * stays unticked.
   *
   * `application.declaration` is deliberately NOT used here even though it is persisted and true:
   * proposer-details sets it unconditionally on its own save, so it is true for every saved quote
   * regardless of this page — reading it would tick a declaration of accuracy that nobody made.
   *
   * The stored flag is still checked first, so this starts reading the real value for free if the
   * API ever adds the field.
   */
  private restoreDeclarationAccepted(): boolean {
    if (this.modelObj?.vehicle_declaration) { return true; }
    return Array.isArray(this.modelObj?.vehicles) && this.modelObj.vehicles.length > 0;
  }

  private restoreDeclarationAnswers() {
    const saved = this.modelObj?.vehicle_declaration_answers || {};
    this.declarationQuestions.forEach(q => { q.answer = saved[q.key] || ''; });
  }

  addVehicle() {
    this.vehicles.push(this.emptyVehicle('horseboxes'));
  }

  removeVehicle(index: number) {
    this.vehicles.splice(index, 1);
  }

  // ─── Named Driver management ───────────────────────────────────────────────

  // 'extended' targets the extended-use (personal/business use) list; anything else
  // is the named-driver list.
  private driverListKey(target: string): string {
    return target === 'extended' ? 'extended_drivers' : 'drivers';
  }

  onDriverAdded(event) {
    const vehicle = this.vehicles[event.vehicleIndex];
    const key = this.driverListKey(event.target);
    if (!vehicle[key]) { vehicle[key] = []; }

    if (event.type === 'existing') {
      // Find driver from root drivers list and add reference
      const existingDriver = (this.modelObj.drivers || []).find(d => d.id === event.driver_id);
      if (existingDriver && !vehicle[key].some(d => d.id === existingDriver.id)) {
        vehicle[key].push({ ...existingDriver });
      }
    } else if (event.type === 'new') {
      // Add to both vehicle and root drivers list — the Drivers page then collects the
      // rest of this driver's mandatory detail.
      vehicle[key].push(event.driver);
      if (!this.modelObj.drivers) { this.modelObj.drivers = []; }
      if (!this.modelObj.drivers.some(d => d.id === event.driver.id)) {
        this.modelObj.drivers.push(event.driver);
      }
    }
  }

  onDriverRemoved(event) {
    const vehicle = this.vehicles[event.vehicleIndex];
    const key = this.driverListKey(event.target);
    if (vehicle[key] && vehicle[key].length > event.driverIndex) {
      vehicle[key].splice(event.driverIndex, 1);
    }
  }

  nextTab() {
    this.declarationTouched = true;

    // Mark every vehicle form as submitted BEFORE the page-level guards below. Those guards
    // return early, so doing it further down meant a quote that trips one of them (e.g. no
    // Agricultural/Horsebox vehicle) showed only the snackbar — none of the per-field
    // "Please select an option." messages appeared, making the mandatory fields look as
    // though they weren't validating at all.
    this.vehicleForms.forEach(vf => vf.submitted = true);

    if (!this.declarationAccepted) {
      this._snackBar.open('Please confirm the declaration before proceeding.', '', { duration: 5000, panelClass: 'text-danger' });
      return;
    }

    // At least one Agricultural or Horsebox vehicle must be present
    const hasRequiredVehicle = this.vehicles.some(v => v.type === 'agricultural' || v.type === 'horseboxes');
    if (!hasRequiredVehicle) {
      this._snackBar.open('At least one Agricultural or Horsebox vehicle must be present', '', { duration: 5000, panelClass: 'text-danger' });
      return;
    }

    // Every vehicle's own form (type-specific mandatory fields, driving restriction, etc.)
    // must be valid — mark each as submitted so its own invalid-field styling appears,
    // and expand every panel so the user can actually see what's missing.
    let allVehicleFormsValid = true;
    this.vehicleForms.forEach(vf => {
      if (vf.frmFields.invalid) { allVehicleFormsValid = false; }
    });
    if (!allVehicleFormsValid) {
      this.expandAll = true;
      this._snackBar.open('Please complete all required fields for every vehicle.', '', { duration: 5000, panelClass: 'text-danger' });
      return;
    }

    // A named-driver restriction (e.g. "Named drivers") requires at least one driver on
    // that vehicle, and ticking the extended personal/business use option requires at
    // least one extended driver, before the user can proceed.
    let missingDrivers = false;
    this.vehicleForms.forEach(vf => {
      if (vf.missingRequiredDrivers) { missingDrivers = true; }
    });
    if (missingDrivers) {
      this.expandAll = true;
      this._snackBar.open('Please add the driver(s) required by each vehicle\'s driving restriction and extended use.', '', { duration: 5000, panelClass: 'text-danger' });
      return;
    }

    // Validate mileage is provided for types that require it
    const mileageTypes = ['horseboxes', 'cars', 'commercial'];
    const missingMileage = this.vehicles.some(v => mileageTypes.includes(v.type) && (v.mileage === null || v.mileage === undefined || v.mileage === ''));
    if (missingMileage) {
      this._snackBar.open('Please enter the annual mileage for all vehicles.', '', { duration: 5000, panelClass: 'text-danger' });
      return;
    }

    // Indicating modifications means at least one has to be named.
    const missingMods = this.vehicles.some(v =>
      v.has_modifications === 'Yes' && (v.mods || []).filter((m: any) => !!m).length === 0);
    if (missingMods) {
      this.expandAll = true;
      this._snackBar.open('Please add at least one modification for every vehicle with non-standard modifications.', '', { duration: 5000, panelClass: 'text-danger' });
      return;
    }

    // Build clean vehicles array with only API fields (strip UI-only helpers)
    const cleanVehicles = this.vehicles.map(v => {
      const vehicle: any = {
        type: v.type,
        // registration_number is mandatory for EVERY vehicle. Trailers and Agri
        // vehicles that aren't road registered are identified by a VIN instead, so it
        // doubles as the registration number — sending only `vin` made the agri-api
        // reject the quote with 007 Missing data /vehicles/N/registration_number.
        registration_number: v.registration_number || v.vin || '',
        vin: v.vin || '',
        make: v.make || '',
        model: v.model || '',
        year: v.year || '',
        value: v.value || null,
        cover_type: v.cover_type || '',
        is_road_registered: v.is_road_registered || '',
        road_registered: v.road_registered || false,
        // is_limited_use rides alongside the boolean the agri-api rates on (same pattern as
        // is_road_registered) so a reopened quote can tell "answered No" apart from "never
        // answered" — the question is mandatory for cars and commercial vehicles.
        is_limited_use: v.is_limited_use || '',
        limited_use: v.limited_use || false,
        driving_restriction: v.driving_restriction || '',
        mileage: (v.mileage !== null && v.mileage !== undefined && v.mileage !== '') ? v.mileage : null,
        tracker: v.tracker || '',
        ncd: v.ncd || '',
        green_card: v.green_card || false,
        is_registered_owner: v.is_registered_owner || '',
        owner: v.owner || '',
        owner_name: v.owner_name || '',
        is_kept_overnight: v.is_kept_overnight || '',
        // postcode is the single overnight postcode the agri-api rates on; the full list
        // (a vehicle can be kept at several addresses) rides alongside it. The API ignores
        // properties it does not know and our own backend stores them, so the extra
        // addresses come back when the quote is reopened.
        postcode: v.postcode || '',
        overnight_addresses: (v.overnight_addresses || []).filter((a: any) => a && (a.postcode || '').toString().trim()),
        has_modifications: v.has_modifications || '',
        extended_use: v.extended_use || { driver_business_use: false, livestock_carriage: false, contract_work: false, horse_transporter: '' },
        mods: (v.mods || []).filter((m: any) => !!m),
        // The agri-api expects plain driver-id STRINGS here (and in extended_drivers).
        // Posting objects makes the calculate/save endpoints return a 500, which the
        // quotation page then reports as a referral with a £0 premium.
        drivers: this.toDriverIds(v.drivers),
        extended_drivers: this.toDriverIds(v.extended_drivers),
      };
      // body_type: all types except trailers (trailers use it as "Trailer Type")
      vehicle.body_type = v.body_type || '';
      // weight: commercial and horseboxes only
      if (v.type === 'commercial' || v.type === 'horseboxes') { vehicle.weight = v.weight || ''; }
      // group: cars only (DVLA lookup value)
      if (v.type === 'cars') { vehicle.group = v.group || null; }
      return vehicle;
    });

    // Drop the legacy typed arrays from modelObj itself (not just the outgoing copy) —
    // otherwise the merge-not-replace fix below resurrects them on every future save.
    delete this.modelObj.agricultural_vehicles;
    delete this.modelObj.cars;
    delete this.modelObj.commercial_vehicles;
    delete this.modelObj.horseboxes;
    delete this.modelObj.trailers;

    // Build attributes: use vehicles[] array, remove old typed arrays
    const attributes = { ...this.modelObj, vehicle_declaration: this.declarationAccepted };
    attributes.vehicles = cleanVehicles;
    attributes.vehicle_declaration_answers = this.declarationQuestions
      .reduce((acc, q) => { acc[q.key] = q.answer; return acc; }, {});

    let data = {
      data: {
        type: this.modelObj.type,
        attributes: attributes,
        id: this.modelObj.id,
        broker_id: this.modelObj.broker_id,
        sequence_number: this.modelObj.sequence_number,
      }
    };

    this.agService.saveQuote(data)
      .subscribe(
        (res: any) => {
          if (res.success) {
            const version = res.result?.quote_data?.quote_versions?.[0];
            const savedApplication = this.agService.applicationOf(version);
            if (version && savedApplication) {
              const meta = this.agService.quoteMetaOf(version);
              // Merge (not replace) — a save response that omits a field must not
              // erase data already entered in another tab (e.g. proposer/fleet experience).
              this.modelObj = { ...this.modelObj, ...savedApplication };
              this.modelObj.id = meta.id;
              this.modelObj.broker_id = version.broker_id;
              this.modelObj.reference = meta.reference;
              this.modelObj.status = meta.status;
              this.modelObj.company_id = version.company_id;
              this.modelObj.sequence_number = version.sequence_number;
              this.modelObj.is_send_email = version.is_send_email;
              if (version.financial_details) {
                this.modelObj.broker_commission = version.financial_details.broker_commission;
                this.modelObj.broker_admin_fee = version.financial_details.broker_admin_fee;
              }
              if (version.policy_holder) {
                this.modelObj.proposer.email = version.policy_holder.email;
                this.modelObj.proposer.phone = version.policy_holder.phone;
                this.modelObj.proposer.is_email = version.policy_holder.is_email;
                this.modelObj.proposer.is_phone = version.policy_holder.is_phone;
                this.modelObj.proposer.sms = version.policy_holder.sms;
                this.modelObj.proposer.post = version.policy_holder.post;
              }
              // Preserve vehicles from form (API response may not include them)
              this.modelObj.vehicles = cleanVehicles;
              this.modelObjUpdated.emit(this.modelObj);
            } else if (res.result && res.result.id) {
              // Minimal response — just update id/sequence_number
              this.modelObj.id = res.result.id;
              if (res.result.sequence_number) { this.modelObj.sequence_number = res.result.sequence_number; }
            }
          } else {
            this._snackBar.open(res.message, '', { duration: 5000, panelClass: 'text-danger' });
          }
        },
        (err) => {
          this._snackBar.open('Error saving vehicles. Please try again.', '', { duration: 5000, panelClass: 'text-danger' });
        }
      );
  }

  prevTab() {
    this.tabChanged.emit(this.modelObj.rating_type == 'fleet' ? 1 : 0);
  }
}
